// Chat-related type definitions

export interface ChatMessage {
  id: number;
  sessionId: number;
  role: "user" | "assistant";
  content: string;
  helpfulnessRating?: string;
  createdAt: string;
}

export interface ChatSession {
  id: number;
  userId: number;
  recipeId?: number;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
}

// New API Contract types

export interface SendMessageRequest {
  message: string;
  conversationId?: string;
  currentRecipeId?: string;
  currentCollectionId?: string;
}

export interface RecipeSuggestion {
  id: string;
  title: string;
  reason?: string;
}

// Recipe write action types — mirror the backend PendingRecipeAction shape

export interface PendingRecipeIngredient {
  name: string;
  quantity: string;
  unit: string;
}

export interface PendingRecipeBody {
  title: string;
  ingredients: PendingRecipeIngredient[];
  instructions: string[];
  tags: string[];
  description?: string;
  prepTime?: string;
  cookTime?: string;
  servings?: string;
}

export interface RecipeChangeDescription {
  description: string;
}

export interface PendingCreateAction {
  type: 'create';
  recipe: PendingRecipeBody;
}

export interface PendingUpdateAction {
  type: 'update';
  recipeId: string;
  recipeTitle: string;
  changes: RecipeChangeDescription[];
  fullUpdatedRecipe: PendingRecipeBody;
}

export interface PendingCollectionCreateAction {
  type: 'collection_create';
  title: string;
  initialRecipeIds: string[];
  initialRecipeNames: string[];
}

export interface PendingCollectionAddAction {
  type: 'collection_add_recipes';
  collectionId: string;
  collectionTitle: string;
  recipeIds: string[];
  recipeNames: string[];
}

export interface PendingCollectionRemoveAction {
  type: 'collection_remove_recipe';
  collectionId: string;
  collectionTitle: string;
  recipeId: string;
  recipeName: string;
}

export interface PendingCollectionRenameAction {
  type: 'collection_rename';
  collectionId: string;
  oldTitle: string;
  newTitle: string;
}

export interface PendingIngredientSubstitutionAction {
  type: 'ingredient_substitution';
  autoSendMessage: string;
}

export type PendingAction =
  | PendingCreateAction
  | PendingUpdateAction
  | PendingCollectionCreateAction
  | PendingCollectionAddAction
  | PendingCollectionRemoveAction
  | PendingCollectionRenameAction
  | PendingIngredientSubstitutionAction;

export function getAutoSendMessage(action: PendingAction | null | undefined): string | undefined {
  return action?.type === 'ingredient_substitution' ? action.autoSendMessage : undefined;
}

export interface ConfirmActionRequest {
  conversationId: string;
  action: PendingAction;
}

export interface RecipeConfirmActionResponse {
  kind: 'recipe';
  recipeId: string;
  recipeTitle: string;
}

export interface CollectionConfirmActionResponse {
  kind: 'collection';
  collectionId: string;
  collectionTitle: string;
}

export type ConfirmActionResponse = RecipeConfirmActionResponse | CollectionConfirmActionResponse;

export interface SendMessageResponse {
  conversationId: string;
  message: string;
  recipeSuggestions?: RecipeSuggestion[];
  pendingAction?: PendingAction;
  timestamp: string;
}
